import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {string | undefined} */
  #currentColor;
  #pendingGallerySelectedColor;

  connectedCallback() {
    super.connectedCallback();

    this.#currentColor = this.dataset.selectedColor;

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.dataset.syncVariantOnMediaSwipe === 'true') {
      this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, {
        signal,
      });
    }

    const behavior = this.dataset.variantMediaBehavior || 'replace';

    if (behavior === 'scroll' && this.#currentColor && this.presentation === 'carousel') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#scrollToColor(this.#currentColor, { animate: false });
        });
      });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles a variant update event.
   * Default Horizon behavior: replace gallery with HTML from response.
   * Custom behavior: keep order and jump to the first media of the selected color.
   *
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const data = event?.detail?.data;
    const behavior = this.dataset.variantMediaBehavior || 'replace';

    const source = data?.html;

    if (source) {
      this.#updateVariantBadge(source);
    }

    // Combined listing / product change: fall back to replacement
    const nextProductId = data?.productId;
    const productChanged = Boolean(nextProductId && this.dataset.productId && nextProductId !== this.dataset.productId);

    if (behavior === 'scroll' && !productChanged && this.#pendingGallerySelectedColor) {
      const variant = event?.detail?.resource || this.#getVariantFromEventData(data);
      const nextColor = variant?.option1 || this.#getSelectedColorFromVariantPicker();

      if (nextColor === this.#pendingGallerySelectedColor) {
        this.#currentColor = nextColor;
        this.#pendingGallerySelectedColor = undefined;
        return;
      }

      this.#pendingGallerySelectedColor = undefined;
    }

    if (behavior === 'scroll' && !productChanged) {
      const variant = event?.detail?.resource || this.#getVariantFromEventData(data);
      const nextColor = variant?.option1 || this.#getSelectedColorFromVariantPicker();

      if (!nextColor) return;

      // Ensure DOM is settled (variant-picker morph may have just happened)
      this.#currentColor = nextColor;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#scrollToColor(nextColor);
        });
      });

      return;
    }

    // Default Horizon: replace from response HTML
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  #updateVariantBadge(source) {
    const currentBadge = this.querySelector('[data-product-media-gallery-badge]');
    const newBadge = source.querySelector('media-gallery [data-product-media-gallery-badge]');

    if (!currentBadge || !newBadge) return;

    currentBadge.innerHTML = newBadge.innerHTML;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Syncs selected product color when the customer manually swipes into another color media chunk.
   * Studio / lifestyle images without data-media-color keep the last selected color.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (this.dataset.syncVariantOnMediaSwipe !== 'true') return;

    const data = event?.detail;

    if (!data?.userInitiated) return;

    const slide = data.slide;
    if (!(slide instanceof HTMLElement)) return;

    const mediaElement = slide.matches('[data-media-scope="main"]')
      ? slide
      : slide.querySelector('[data-media-scope="main"]');

    if (!(mediaElement instanceof HTMLElement)) return;

    const color = mediaElement.dataset.mediaColor;

    // Studio / Tragebeispiele: keine Farbe ändern
    if (!color) return;

    // Gleicher Farbchunk: nichts tun
    if (color === this.#currentColor) return;

    this.#currentColor = color;
    this.#pendingGallerySelectedColor = color;

    this.#selectVariantColor(color);
  };

  /**
   * Selects option 1 / color in the product variant picker.
   * Uses the normal change event so Horizon updates price, URL, ATC state, sticky bar, etc.
   *
   * @param {string} color
   */
  #selectVariantColor(color) {
    const root = this.closest('.shopify-section, dialog') || document;
    const variantPicker = root.querySelector('variant-picker');

    if (!variantPicker) return;

    const colorRadio = Array.from(
      variantPicker.querySelectorAll('input[type="radio"][data-input-id^="1-"]')
    ).find((input) => input instanceof HTMLInputElement && input.value === color);

    if (colorRadio instanceof HTMLInputElement) {
      if (colorRadio.checked) return;
      if (colorRadio.getAttribute('aria-disabled') === 'true') return;

      colorRadio.checked = true;
      colorRadio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const colorOption = Array.from(
      variantPicker.querySelectorAll('select option[data-input-id^="1-"]')
    ).find((option) => option instanceof HTMLOptionElement && option.value === color);

    if (colorOption instanceof HTMLOptionElement) {
      const select = colorOption.closest('select');

      if (!(select instanceof HTMLSelectElement)) return;
      if (select.value === colorOption.value) return;

      select.value = colorOption.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Extract variant object from event data (supports Horizon-shaped payloads).
   * @param {any} data
   */
  #getVariantFromEventData(data) {
    if (!data) return undefined;
    if (data.variant && typeof data.variant === 'object') return data.variant;
    return data;
  }

  /**
   * Fallback: derive selected color (option1) directly from the variant-picker DOM.
   * Works with radio buttons and dropdowns.
   * @returns {string | undefined}
   */
  #getSelectedColorFromVariantPicker() {
    const root = this.closest('.shopify-section, dialog') || document;

    const checkedColorInput = root.querySelector(
      'variant-picker input[type="radio"][data-input-id^="1-"]:checked'
    );

    if (checkedColorInput instanceof HTMLInputElement) {
      return checkedColorInput.value;
    }

    const selectedColorOption = root.querySelector('variant-picker select option[selected][data-input-id^="1-"]');

    if (selectedColorOption instanceof HTMLOptionElement) {
      return selectedColorOption.value;
    }

    return undefined;
  }

  /**
   * Jump to the first media item for a given color.
   * - carousel: select slide
   * - grid: scroll into view
   *
   * @param {string} color
   */
  #scrollToColor(color, options = {}) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animate = options.animate ?? !reduceMotion;

    const escape = (value) => {
      if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(value);
      return String(value).replace(/["\\]/g, '\\$&');
    };

    const selector = `[data-media-scope="main"][data-first-color-media="true"][data-media-color="${escape(color)}"]`;
    const target = this.querySelector(selector);

    if (!target || !(target instanceof HTMLElement)) return;

    const index = Number.parseInt(target.dataset.mediaIndex || '', 10);

    if (this.presentation === 'carousel' && !Number.isNaN(index)) {
      this.slideshow?.select(index, undefined, { animate });
      return;
    }

    target.scrollIntoView({ behavior: animate ? 'smooth' : 'auto', block: 'start' });
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
